import { useCan } from '@features/auth';
import {
    Badge,
    Button,
    Card,
    Chip,
    EmptyState,
    ErrorState,
    FlagKeyPill,
    IconArrowRight,
    IconPlus,
    IconSearch,
    LoadingRows,
    PageHeader,
    PropagationNotice,
    Toggle,
    WriteError,
} from '@shared/ui';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { defaultWriteFrom } from '../api/default-write';
import { useFlags, useSetDefault } from '../hooks/use-flags';
import { LIFECYCLE_LABEL, LIFECYCLE_ORDER, LIFECYCLE_TONE } from '../schemas/lifecycle';

import { CreateFlagDialog } from './CreateFlagDialog';
import { ReadOnlyNotice } from './ReadOnlyNotice';

import type { FlagLifecycle, FlagListItem } from '../schemas/flag';

const NO_ROWS: readonly FlagListItem[] = [];

export function FlagsPage() {
    const navigate = useNavigate();
    const flags = useFlags();
    const [lifecycle, setLifecycle] = useState<FlagLifecycle | 'all'>('all');
    const [search, setSearch] = useState('');
    const [creating, setCreating] = useState(false);
    // Must match `@RequiresRole('engineer')` on `POST /api/flags` — a key is
    // permanent, so creation sits above the ops tier.
    const canCreate = useCan('engineer');

    // A `?? []` inline would mint a new array on every render and defeat both
    // memos below — they would recompute on every keystroke anywhere in the tree.
    const rows = flags.data ?? NO_ROWS;

    /**
     * Counts come from the loaded rows rather than a separate endpoint. The
     * mock hard-coded them ("All 32", "GA 21") against a seven-row sample, so
     * the chips disagreed with the table underneath them. Deriving guarantees
     * they cannot.
     */
    const counts = useMemo(() => {
        const byLifecycle = new Map<FlagLifecycle, number>();
        for (const row of rows) {
            byLifecycle.set(
                row.definition.lifecycle,
                (byLifecycle.get(row.definition.lifecycle) ?? 0) + 1,
            );
        }
        return byLifecycle;
    }, [rows]);

    const visible = useMemo(() => {
        const needle = search.trim().toLowerCase();

        return rows
            .filter((row) => lifecycle === 'all' || row.definition.lifecycle === lifecycle)
            .filter(
                (row) =>
                    needle.length === 0 ||
                    row.definition.key.toLowerCase().includes(needle) ||
                    row.definition.name.toLowerCase().includes(needle),
            );
    }, [rows, lifecycle, search]);

    return (
        <>
            <PageHeader
                eyebrow="Configuration"
                title="Feature Flags"
                description="Flags are created here at runtime and stored in DynamoDB — there is no code catalog. Every change is audited against the operator who made it."
                actions={
                    <Button
                        disabled={!canCreate}
                        onClick={() => {
                            setCreating(true);
                        }}
                    >
                        <IconPlus className="size-4" />
                        New flag
                    </Button>
                }
            />

            <ReadOnlyNotice />

            <Card>
                <div className="flex flex-wrap items-center gap-2.5 border-b border-gray-100 px-5 py-3">
                    <Chip
                        active={lifecycle === 'all'}
                        onClick={() => {
                            setLifecycle('all');
                        }}
                    >
                        All<b className="ml-0.5">{rows.length}</b>
                    </Chip>
                    {LIFECYCLE_ORDER.map((value) => (
                        <Chip
                            key={value}
                            active={lifecycle === value}
                            onClick={() => {
                                setLifecycle(value);
                            }}
                        >
                            {LIFECYCLE_LABEL[value]}
                            <b className="ml-0.5">{counts.get(value) ?? 0}</b>
                        </Chip>
                    ))}
                    <label className="ml-auto flex min-w-56 items-center gap-2 rounded-lg bg-gray-100 px-3 py-1.5 text-gray-500">
                        <IconSearch className="size-4" />
                        <span className="sr-only">Filter flags by key or name</span>
                        <input
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value);
                            }}
                            className="w-full bg-transparent text-[12.5px] outline-none placeholder:text-gray-500"
                            placeholder="Filter by key or name…"
                        />
                    </label>
                </div>

                {flags.isPending ? (
                    <LoadingRows label="Loading flags…" />
                ) : flags.isError ? (
                    <ErrorState error={flags.error} onRetry={() => void flags.refetch()} />
                ) : rows.length === 0 ? (
                    <EmptyState
                        title="No flags yet"
                        description="Nothing has been created in this account's table. A flag referenced in backend code does not appear here until someone creates its row."
                        action={
                            <Button
                                disabled={!canCreate}
                                onClick={() => {
                                    setCreating(true);
                                }}
                            >
                                <IconPlus className="size-4" />
                                Create the first flag
                            </Button>
                        }
                    />
                ) : visible.length === 0 ? (
                    <EmptyState
                        title="Nothing matches those filters"
                        description="Try a different lifecycle, or clear the search box."
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                                    <th className="px-5 py-3 font-semibold">Flag</th>
                                    <th className="px-5 py-3 font-semibold">Category</th>
                                    <th className="px-5 py-3 font-semibold">Lifecycle</th>
                                    <th className="px-5 py-3 font-semibold">Default</th>
                                    <th className="px-5 py-3 font-semibold">Rollout</th>
                                    <th className="px-5 py-3 font-semibold">Owner</th>
                                    <th className="px-5 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((row) => (
                                    <FlagRow key={row.definition.key} row={row} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <PropagationNotice className="mt-4 text-center text-xs text-gray-400" />

            <CreateFlagDialog
                open={creating}
                onClose={() => {
                    setCreating(false);
                }}
                onCreated={(key) => {
                    setCreating(false);
                    // Straight to the flag that was just created: it exists but
                    // is off and has no overrides, so the detail view is where
                    // the next thing an operator wants to do actually lives.
                    void navigate(`/flags/${encodeURIComponent(key)}`);
                }}
            />
        </>
    );
}

/**
 * One row, owning its own default-toggle mutation.
 *
 * Per-row rather than one mutation hoisted to the page, so an in-flight write on
 * one flag disables only that flag's switch. A shared mutation would grey out
 * every toggle in the table on any click, and — worse — a failure would attach
 * its error to whichever row rendered it last.
 */
function FlagRow({ row }: { row: FlagListItem }) {
    const { definition, default: current } = row;
    const setDefault = useSetDefault(definition.key);
    // Matches `@RequiresRole('engineer')` on `PUT /api/flags/:key/default` —
    // the platform default is every museum without an override.
    const canSetDefault = useCan('engineer');

    return (
        <>
            <tr className="border-t border-gray-100 text-[13.5px] hover:bg-gray-50">
                <td className="px-5 py-3">
                    <Link
                        to={`/flags/${encodeURIComponent(definition.key)}`}
                        className="inline-block"
                    >
                        <FlagKeyPill>{definition.key}</FlagKeyPill>
                    </Link>
                    <div className="mt-0.5 text-xs text-gray-500">{definition.name}</div>
                </td>
                <td className="px-5 py-3 text-gray-700">{definition.category}</td>
                <td className="px-5 py-3">
                    <Badge tone={LIFECYCLE_TONE[definition.lifecycle]} dot>
                        {LIFECYCLE_LABEL[definition.lifecycle]}
                    </Badge>
                </td>
                <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                        <Toggle
                            on={current.enabled}
                            busy={setDefault.isPending}
                            // An archived flag resolves `false` everywhere no
                            // matter what this says, so letting it be toggled
                            // would show a change that changes nothing.
                            disabled={current.archived}
                            // Omitting `onChange` is `Toggle`'s read-only mode:
                            // it renders inert rather than accepting a click
                            // the API would refuse.
                            onChange={
                                canSetDefault
                                    ? (next) => {
                                          setDefault.mutate(
                                              defaultWriteFrom(current, { enabled: next }),
                                          );
                                      }
                                    : undefined
                            }
                            aria-label={`Platform default for ${definition.key}`}
                        />
                        {current.archived && <Badge tone="neutral">Archived</Badge>}
                    </div>
                </td>
                <td className="px-5 py-3 text-gray-700">
                    {current.rolloutPercentage === undefined ? (
                        <span className="text-xs text-gray-400">No gate</span>
                    ) : (
                        <span className="font-semibold">{current.rolloutPercentage}%</span>
                    )}
                </td>
                <td className="px-5 py-3 text-gray-700">{definition.owner}</td>
                <td className="px-5 py-3 text-right">
                    <Link
                        to={`/flags/${encodeURIComponent(definition.key)}`}
                        aria-label={`Open ${definition.key}`}
                        className="text-gray-400 hover:text-brand-600"
                    >
                        <IconArrowRight className="inline size-4" />
                    </Link>
                </td>
            </tr>
            {setDefault.error && (
                <tr className="border-t border-gray-100">
                    <td colSpan={7} className="px-5 py-2.5">
                        <WriteError error={setDefault.error} />
                    </td>
                </tr>
            )}
        </>
    );
}
